import {
  faBoxArchive,
  faCircleQuestion,
  faCopy,
  faEye,
  faPen,
  faPlus,
  faStar,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { fetchAdminAttachmentTemplates } from '@/api/orders';
import type { AdminAttachmentTemplateRow } from '@/api/orders/types';
import { AppLink } from '@/components/shared/AppLink';
import { DataTable } from '@/components/shared/DataTable';
import type { ColumnDef } from '@/components/shared/DataTable/tanstack';
import type { FilterDef, RowAction } from '@/components/shared/DataTable/types';
import { PageContainer } from '@/components/shared/PageContainer';
import { UpdatedAtValue } from '@/components/shared/ValueRenderers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/hooks/usePermissions';
import { ATTACHMENT_TEMPLATE_TRACING_TYPES } from '@/lib/constants/attachmentTemplate';
import { queryClient } from '@/lib/utils/queryClient';
import type { AttachmentTemplateVisibility } from '../../-hooks/useAdminAttachmentTemplates';
import { useAttachmentTemplatesListController } from '../../-hooks/useAttachmentTemplatesListController';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/attachment-templates/',
)({
  validateSearch: (search: Record<string, unknown>) => ({
    sort: typeof search.sort === 'string' ? search.sort : undefined,
    visibility:
      typeof search.visibility === 'string' ? search.visibility : undefined,
  }),
  loader: () =>
    queryClient.ensureQueryData({
      queryKey: [
        'orders',
        'adminAttachmentTemplates',
        { page: 1, count: 50, templateType: 'PRODUCT_BACKWARD' },
      ],
      queryFn: () => {
        const qs = new URLSearchParams({
          page: '1',
          count: '50',
          templateType: 'PRODUCT_BACKWARD',
        }).toString();
        return fetchAdminAttachmentTemplates(qs);
      },
    }),
  component: AttachmentTemplatesListPage,
});

function AttachmentTemplatesListPage() {
  const navigate = Route.useNavigate();
  // Templates are part of the orders resource — there is no template
  // permission of its own; the routes under /attachment-templates guard on
  // `orders`.
  const perms = usePermissions().orders;
  const [archiveTarget, setArchiveTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const {
    t,
    table,
    rows,
    total,
    isLoading,
    visibility,
    setVisibility,
    preferenceMutation,
    cloneMutation,
    archiveMutation,
    handleSetDefault,
    handleClone,
    handleArchive,
  } = useAttachmentTemplatesListController();

  // 'all' added automatically by DataTable QuickFiltersBar
  const quickFilters: { id: AttachmentTemplateVisibility; label: string }[] = [
    { id: 'global', label: t('list.filterGlobal') },
    { id: 'private', label: t('list.filterPrivate') },
    { id: 'shared', label: t('list.filterShared') },
  ];

  const columns: ColumnDef<AdminAttachmentTemplateRow, unknown>[] = [
    {
      id: 'name',
      header: t('list.colName'),
      accessorKey: 'name',
      enableSorting: true,
      cell: ({ row }) => {
        const r = row.original;
        const isShared = !r.isGlobal && !!r.owner;
        return (
          <span className='flex items-center gap-2 min-w-0'>
            <AppLink
              to='/orders/attachment-templates/$id'
              params={{ id: r.id }}
              bold
            >
              {r.name}
            </AppLink>
            {r.isGlobal ? (
              <Badge variant='info' className='rounded-md shrink-0'>
                {t('list.global')}
              </Badge>
            ) : isShared ? (
              <Badge variant='warning' className='rounded-md shrink-0'>
                {t('list.shared')}
              </Badge>
            ) : null}
          </span>
        );
      },
      size: 280,
    },
    {
      id: 'tracingType',
      header: t('list.colTracingType'),
      accessorKey: 'tracingType',
      enableSorting: false,
      cell: ({ getValue }) => {
        const key = getValue<string>();
        const label = ATTACHMENT_TEMPLATE_TRACING_TYPES[key] ?? key;
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs font-medium'>
            {label}
          </span>
        );
      },
      size: 200,
    },
    {
      id: 'isPreference',
      header: t('list.colDefault'),
      accessorKey: 'isPreference',
      enableSorting: false,
      cell: ({ getValue }) =>
        getValue<boolean>() ? (
          <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium'>
            <FontAwesomeIcon icon={faStar} className='size-3' />
            {t('list.default')}
          </span>
        ) : null,
      size: 140,
    },
    {
      id: 'updatedAt',
      header: t('list.colUpdatedAt'),
      accessorKey: 'updatedAt',
      enableSorting: true,
      cell: ({ row }) => (
        <UpdatedAtValue
          value={row.original.updatedAt}
          userName={row.original.updatedByUserName}
          companyName={row.original.updatedByCompanyName}
        />
      ),
      size: 200,
    },
  ];

  const rowActions: RowAction<AdminAttachmentTemplateRow>[] = [
    {
      label: t('list.actionView'),
      icon: <FontAwesomeIcon icon={faEye} className='size-3.5' />,
      onClick: row =>
        void navigate({
          to: '/orders/attachment-templates/$id',
          params: { id: row.id },
        }),
    },
    {
      label: t('list.actionEdit'),
      icon: <FontAwesomeIcon icon={faPen} className='size-3.5' />,
      hidden: () => !perms.canEdit,
      onClick: row =>
        void navigate({
          to: '/orders/attachment-templates/$id/edit',
          params: { id: row.id },
        }),
    },
    {
      label: t('list.actionClone'),
      icon: <FontAwesomeIcon icon={faCopy} className='size-3.5' />,
      // A clone is a new template, then straight into its editor.
      hidden: () => !perms.canCreate || !perms.canEdit,
      onClick: async row => {
        const newId = await handleClone(row.id);
        if (newId) {
          void navigate({
            to: '/orders/attachment-templates/$id/edit',
            params: { id: newId },
          });
        }
      },
      loading: row =>
        cloneMutation.isPending && cloneMutation.variables === row.id,
    },
    {
      label: t('list.setDefault'),
      icon: <FontAwesomeIcon icon={faStar} className='size-3.5' />,
      hidden: row => !perms.canEdit || row.isPreference || !!row.owner,
      onClick: row => void handleSetDefault(row.id),
      loading: row =>
        preferenceMutation.isPending && preferenceMutation.variables === row.id,
    },
    {
      label: t('list.actionArchive'),
      icon: <FontAwesomeIcon icon={faBoxArchive} className='size-3.5' />,
      // Archiving a template is guarded on `orders:delete` — it is one-way,
      // there is no route back.
      hidden: row => !perms.canDelete || row.isGlobal,
      onClick: row => setArchiveTarget({ id: row.id, name: row.name }),
    },
  ];

  const filterDefs: FilterDef<AdminAttachmentTemplateRow>[] = [
    {
      id: 'name',
      label: t('list.colName'),
      type: 'text',
    },
  ];

  return (
    <PageContainer>
      <DataTable
        storageKey='orders-attachment-templates'
        data={rows}
        columns={columns}
        loading={isLoading}
        rowActions={rowActions}
        filterDefs={filterDefs}
        rowIdKey='id'
        emptyMessage={t('list.empty')}
        sorting={table.sorting}
        onSortingChange={table.onSortingChange}
        filters={table.filters}
        onFiltersChange={table.onFiltersChange}
        pagination={{ pageIndex: table.page - 1, pageSize: table.limit }}
        onPaginationChange={({ pageIndex, pageSize }) =>
          table.onPaginationChange({ pageIndex, pageSize })
        }
        manualPageCount={Math.ceil(total / table.limit)}
        quickFilters={quickFilters}
        activeQuickFilter={visibility}
        onQuickFilterChange={id =>
          setVisibility(id as AttachmentTemplateVisibility)
        }
        toolbarActions={[
          ...(perms.canCreate
            ? [
                {
                  type: 'custom' as const,
                  id: 'create',
                  content: (
                    <Button
                      onClick={() =>
                        void navigate({
                          to: '/orders/attachment-templates/create',
                        })
                      }
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      {t('list.create')}
                    </Button>
                  ),
                },
              ]
            : []),
          {
            type: 'button',
            id: 'help',
            label: t('list.helpTooltip'),
            icon: <FontAwesomeIcon icon={faCircleQuestion} />,
            onClick: () => {},
          },
        ]}
      />

      {/* Archive confirm dialog */}
      <AlertDialog
        open={!!archiveTarget}
        onOpenChange={open => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.archiveTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.archiveDescription', {
                name: archiveTarget?.name ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setArchiveTarget(null)}
              disabled={archiveMutation.isPending}
            >
              {t('detail.archiveCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant='destructive'
              loading={archiveMutation.isPending}
              onClick={async () => {
                if (!archiveTarget) return;
                const ok = await handleArchive(
                  archiveTarget.id,
                  archiveTarget.name,
                );
                if (ok) setArchiveTarget(null);
              }}
            >
              {t('detail.archiveConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
